import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  Button,
  Alert,
  Spinner,
  Table,
  Form,
  Row,
  Col,
  Badge,
  InputGroup
} from 'react-bootstrap';
import {
  FaDownload,
  FaUpload,
  FaDatabase,
  FaTrash,
  FaSync
} from 'react-icons/fa';

const BackupRestore = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [professionalLoading, setProfessionalLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/backup.php?action=list');
      setBackups(response.data.backups || []);
    } catch (error) {
      setBackups([]);
    } finally {
      setLoading(false);
    }
  };

  const createProfessionalBackup = async () => {
    try {
      setProfessionalLoading(true);
      setMessage(null);
      
      const response = await axios.get('/api/manual-professional-backup.php');
      
      setMessage({
        type: 'success',
        text: '✅ Professional backup created successfully! (Compressed + Encrypted + Email sent)'
      });
      
      setTimeout(() => fetchBackups(), 3000);
    } catch (error) {
      setMessage({
        type: 'danger',
        text: 'Failed to create professional backup: ' + (error.response?.data?.error || error.message)
      });
    } finally {
      setProfessionalLoading(false);
    }
  };

  const createBackup = async () => {
    try {
      setLoading(true);
      setMessage(null);
      
      const response = await axios.post('/api/backup.php?action=create');
      
      setMessage({
        type: 'success',
        text: response.data.message || 'Backup started'
      });
      
      setTimeout(() => fetchBackups(), 2000);
    } catch (error) {
      setMessage({
        type: 'danger',
        text: 'Failed to create backup: ' + (error.response?.data?.error || error.message)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.name.endsWith('.sql') || file.name.endsWith('.backup') || file.name.endsWith('.gz') || file.name.endsWith('.enc'))) {
      setSelectedFile(file);
      setMessage(null);
    } else {
      setMessage({
        type: 'warning',
        text: 'Please select a valid backup file (.sql, .backup, .gz, .enc)'
      });
      setSelectedFile(null);
    }
  };

  const restoreBackup = async () => {
    if (!selectedFile) {
      setMessage({
        type: 'warning',
        text: 'Please select a backup file first'
      });
      return;
    }

    if (!window.confirm('Are you sure you want to restore this backup? This will replace all current data!')) {
      return;
    }

    try {
      setUploading(true);
      setMessage(null);

      const formData = new FormData();
      formData.append('backupFile', selectedFile);

      // Check if encrypted file
      const isEncrypted = selectedFile.name.endsWith('.enc');
      const endpoint = isEncrypted ? '/api/restore-encrypted.php' : '/api/backup.php?action=restore';

      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 600000, // 10 minutes timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('Upload progress:', percentCompleted + '%');
        }
      });

      if (response.data.success) {
        const details = response.data.details;
        let message = 'Database restored successfully!';
        
        if (details) {
          message += `\n\nQueries executed: ${details.queries_executed}`;
          if (details.errors > 0) {
            message += `\nWarnings: ${details.errors}`;
          }
        }
        
        setMessage({
          type: 'success',
          text: message + ' The application will reload automatically.'
        });
        
        // Auto reload after 3 seconds
        setTimeout(() => {
          window.location.reload();
        }, 3000);
        setSelectedFile(null);
        document.getElementById('backup-file-input').value = '';
      } else {
        setMessage({
          type: 'danger',
          text: response.data.message || 'Failed to restore backup'
        });
      }
    } catch (error) {
      setMessage({
        type: 'danger',
        text: 'Failed to restore backup: ' + (error.response?.data?.message || error.message)
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      console.log('Downloading:', filename);
      
      const response = await fetch(`/api/backup.php?action=download&file=${encodeURIComponent(filename)}`);
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setMessage({
        type: 'success',
        text: `Backup downloaded: ${filename}`
      });
      
    } catch (error) {
      console.error('Download error:', error);
      setMessage({
        type: 'danger',
        text: `Download failed: ${error.message}`
      });
    }
  };

  const deleteBackup = async (filename) => {
    console.log('Delete clicked:', filename);
    
    if (!window.confirm(`Are you sure you want to delete backup: ${filename}?`)) {
      return;
    }

    try {
      setLoading(true);
      console.log('Sending delete request');
      
      const response = await axios.delete(`/api/backup.php?action=delete&file=${encodeURIComponent(filename)}`);
      
      console.log('Delete response:', response.data);
      
      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Backup deleted: ${filename}`
        });
        fetchBackups();
      } else {
        setMessage({
          type: 'danger',
          text: response.data.message || 'Failed to delete backup'
        });
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({
        type: 'danger',
        text: 'Failed to delete backup: ' + (error.response?.data?.message || error.message)
      });
    } finally {
      setLoading(false);
    }
  };



  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Database Backup & Restore</h2>
        <Button variant="outline-secondary" onClick={fetchBackups} disabled={loading}>
          <FaSync className="me-2" />
          Refresh
        </Button>
      </div>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <FaDatabase className="me-2" />
                Quick Backup (Simple)
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted">
                Create a simple SQL backup instantly.
                <br/>
                <strong>Features:</strong> Fast, plain SQL file, no compression.
              </p>
              <Button 
                variant="primary" 
                onClick={createBackup} 
                disabled={loading}
                className="w-100"
              >
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FaDownload className="me-2" />
                    Quick Backup
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-4 border-success">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">
                <FaDatabase className="me-2" />
                Professional Backup ⭐
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted">
                Create a professional backup with advanced features.
                <br/>
                <strong>Features:</strong> Compressed (70% smaller), Encrypted (AES-256), Email notification, Backup verification.
              </p>
              <Button 
                variant="success" 
                onClick={createProfessionalBackup} 
                disabled={professionalLoading}
                className="w-100"
              >
                {professionalLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Creating Professional Backup...
                  </>
                ) : (
                  <>
                    <FaDownload className="me-2" />
                    Professional Backup ⭐
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">
                <FaUpload className="me-2" />
                Restore Backup
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted">
                Upload and restore a SQL backup file. <strong>WARNING:</strong> This will completely replace all current data!
              </p>
              <Form.Group className="mb-3">
                <Form.Control
                  type="file"
                  accept=".sql,.backup,.gz,.enc"
                  onChange={handleFileSelect}
                  id="backup-file-input"
                />
                <Form.Text className="text-muted">
                  Select a .sql, .backup, .gz, or .enc file to restore
                </Form.Text>
              </Form.Group>
              {selectedFile && (
                <Alert variant="info" className="mb-3">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </Alert>
              )}
              <Button 
                variant="warning" 
                onClick={restoreBackup} 
                disabled={!selectedFile || uploading}
                className="w-100"
              >
                {uploading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <FaUpload className="me-2" />
                    Restore Backup
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>
          <h5 className="mb-0">Available Backups</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading backups...</p>
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <FaDatabase size={48} className="mb-3" />
              <p>No backups found. Create your first backup above.</p>
            </div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Size</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup, index) => (
                  <tr key={index}>
                    <td>
                      <code>{backup.name}</code>
                    </td>
                    <td>
                      <Badge bg="secondary">{backup.size}</Badge>
                    </td>
                    <td>
                      {new Date(backup.date).toLocaleString()}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => downloadBackup(backup.name)}
                        className="me-2"
                      >
                        <FaDownload className="me-1" />
                        Download
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => deleteBackup(backup.name)}
                        disabled={loading}
                      >
                        <FaTrash className="me-1" />
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default BackupRestore;
